import { Component } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from "ag-grid-community";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";

@Component({
    standalone: true,
    imports: [CommonModule, RouterModule],
    templateUrl: './audit-changes-button.component.html',
})
export class AuditChangesButtonComponent implements ICellRendererAngularComp {

    fullRowData: any;
    oldValues: any;
    newValues: any;
    parsedOldValues: any = {};
    parsedNewValues: any = {};
    friendlyNames: any = {};
    recordUrl: any;
    requiredFields: string[] = [];
    createdBy: any;
    createdDate: any;
    modifiedBy: any;
    modifiedDate: any;

    constructor(private modalService: NgbModal) { }

    agInit(params: ICellRendererParams<any, any, any>): void {
        this.fullRowData = params.data;
        this.recordUrl = this.generateRecordUrl(params.data.entityName, params.data.entityId, params.data);
        this.friendlyNames = params.data.fieldFriendlyNames || {};

        // Ensure key fields are always displayed
        this.requiredFields = ["Id", "CreatedBy", "CreatedDate", "ModifiedBy", "ModifiedDate"];

        this.parsedOldValues = this.parseJson(params.data.oldValues);
        this.parsedNewValues = this.parseJson(params.data.newValues);

        //  Extract metadata fields
        this.createdBy = this.parsedNewValues["CreatedBy"] || this.parsedOldValues["CreatedBy"] || "N/A";
        this.createdDate = this.parsedNewValues["CreatedDate"] || this.parsedOldValues["CreatedDate"] || "N/A";
        this.modifiedBy = this.parsedNewValues["ModifiedBy"] || this.parsedOldValues["ModifiedBy"] || null;
        this.modifiedDate = this.parsedNewValues["ModifiedDate"] || this.parsedOldValues["ModifiedDate"] || null;
    }

    /**  Helper method to safely parse JSON */
    private parseJson(jsonString: string | object | null): any {
        if (!jsonString) return {};
        if (typeof jsonString === "object") return jsonString; // Already parsed
        try {
            return JSON.parse(jsonString);
        } catch {
            return {};
        }
    }

    /** Returns fields to display */
    get displayedFields(): string[] {
        const excludedFields = ["CreatedBy", "CreatedDate", "ModifiedBy", "ModifiedDate", "ModifiedById", "CreatedById", "OrganizationId"];


        // Show all new fields if operation is "Added"
        if (this.fullRowData.operation === "Added") {
            return Object.keys(this.parsedNewValues).filter(key => !excludedFields.includes(key));
        }

        //  Otherwise, show only changed + required fields
        const changedFields = Object.keys(this.parsedOldValues).filter(
            (key) => !excludedFields.includes(key) && !this.areValuesEqual(this.parsedOldValues[key], this.parsedNewValues[key])
        );

        //return [...new Set([...this.requiredFields, ...changedFields])]; //  Always show required fields
        return [...new Set(changedFields)];
    }

    /** Improved comparison for deep equality */
    private areValuesEqual(oldValue: any, newValue: any): boolean {
        if (oldValue === newValue) return true; //  Exact match

        //  Handle cases where one is a string and the other is null/undefined
        if ((oldValue == null && newValue === "NULL") || (newValue == null && oldValue === "NULL")) {
            return true;
        }

        //  Convert to string and trim for fair comparison
        if (typeof oldValue === "string" && typeof newValue === "string") {
            return oldValue.trim() === newValue.trim();
        }

        //  Handle objects and arrays using JSON.stringify
        if (typeof oldValue === "object" && typeof newValue === "object") {
            return JSON.stringify(oldValue) === JSON.stringify(newValue);
        }

        return false; // Consider different if no conditions match
    }


    refresh(params: ICellRendererParams<any, any, any>): boolean {
        this.agInit(params);
        return true;
    }

    openJsonModal(content: any) {
        this.modalService.open(content, { size: 'lg', scrollable: true, fullscreen: true });
    }

    get changedFields(): string[] {
        return [...new Set([...Object.keys(this.parsedOldValues), ...Object.keys(this.parsedNewValues || {})])];
    }

    /**  Generate record URL based on entity type */
    generateRecordUrl(entityName: string, entityId: string, data: any): string {        
        switch (entityName.toLowerCase()) {
            case 'department':
                return `/departments/edit/${entityId}`;
            case 'servicearea':
                return `/service-areas/edit/${entityId}`;
            case 'knowledgebaseitem':
                return `/service-areas/${entityId}/knowledge-base-items`;
            case 'scheduleperiod':
                return `/departments/edit/${data.oldValues.DepartmentId || data.newValues.DepartmentId}`;
            default:
                return ''; // No valid URL mapping
        }
    }

    isChanged(key: string): boolean {
        const oldValue = this.parsedOldValues[key] ?? '';
        const newValue = this.parsedNewValues[key] ?? '';

        // Convert objects and arrays to consistent JSON strings
        const normalize = (value: any) => {
            if (typeof value === 'string') {
                try {
                    return JSON.stringify(JSON.parse(value)); // Normalize JSON strings
                } catch {
                    return value.trim(); // Trim strings if not JSON
                }
            }
            return JSON.stringify(value); // Convert objects/arrays to JSON
        };

        return normalize(oldValue) !== normalize(newValue);
    }
}